import React from 'react';
import { Dialog, DialogContent, DialogTitle, DialogHeader } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Squares2X2Icon,
  ShoppingCartIcon,
  MinusIcon,
  UserGroupIcon,
  QrCodeIcon,
  WindowIcon,
  LinkIcon,
} from '@heroicons/react/24/solid';

const URL_SHARE_GATED_TOOLTIP =
  'Stripe is reviewing your account. Sharing will be available shortly.';

const ElementSelectionModal = ({ open, setOpen, onElementTypeSelect, shareBlocked = false }) => {
  // Calculate number of visible elements for dynamic grid layout
  const visibleElementsCount =
    1 + // Add to Cart (always visible)
    4 + // Fundraising Bar, Supporter List, QR Code, Fundraising Flag (always visible)
    1;  // URL (always visible)

  // Determine responsive grid columns
  // Mobile: 1 column, sm-md: 2 columns, md+: 3 columns
  const getGridCols = () => {
    if (visibleElementsCount === 1) return 'grid-cols-1';
    if (visibleElementsCount === 2) return 'grid-cols-1 sm:grid-cols-2';
    return 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3'; // 3 or more elements
  };

  const handleElementTypeSelect = (elementType) => {
    // Pass ALL element types to parent
    onElementTypeSelect(elementType);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex gap-2">
            <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-paper-inset sm:mx-0 sm:h-10 sm:w-10">
              <Squares2X2Icon className="h-6 w-6 text-ink-muted" />
            </div>
            <div className="flex flex-col items-start w-full">
              <DialogTitle className="text-md leading-6 text-ink-muted">Create Element</DialogTitle>
              <p className="text-sm leading-6 text-ink-muted">
                Select an element type to embed on your website
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className={`mt-3 grid ${getGridCols()} gap-4`}>
          {/* Position 1: Add to Cart */}
          <div
            onClick={() => handleElementTypeSelect('add-to-cart')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleElementTypeSelect('add-to-cart');
              }
            }}
            role="button"
            tabIndex={0}
            className="group cursor-pointer rounded-lg border-2 border-edge bg-paper-inset p-2 sm:p-6 transition-all duration-200 hover:border-ink-muted hover:bg-paper-muted focus:outline-none focus:ring-2 focus:ring-sage"
          >
            <div className="flex items-start gap-4 sm:flex-col sm:items-center sm:gap-0 sm:text-center">
              <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full sm:mx-auto sm:mb-1.5">
                <ShoppingCartIcon className="h-12 w-12 text-ink-secondary" />
              </div>
              <div className="flex-1 sm:w-full">
                <h3 className="text-sm font-semibold text-ink group-hover:text-ink">Add to Cart</h3>
                <p className="mt-1 text-xs text-ink-muted group-hover:text-ink-secondary">
                  Embeddable button that lets visitors add items to their cart from any page
                </p>
              </div>
            </div>
          </div>

          {/* Position 2: Fundraise Bar */}
          <div
            onClick={() => handleElementTypeSelect('fundraise-bar')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleElementTypeSelect('fundraise-bar');
              }
            }}
            role="button"
            tabIndex={0}
            className="group cursor-pointer rounded-lg border-2 border-edge bg-paper-inset p-2 sm:p-6 transition-all duration-200 hover:border-ink-muted hover:bg-paper-muted focus:outline-none focus:ring-2 focus:ring-sage"
          >
            <div className="flex items-start gap-4 sm:flex-col sm:items-center sm:gap-0 sm:text-center">
              <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full sm:mx-auto sm:mb-1.5">
                <MinusIcon className="h-12 w-12 text-ink-secondary" />
              </div>
              <div className="flex-1 sm:w-full">
                <h3 className="text-sm font-semibold text-ink group-hover:text-ink">
                  Fundraising Bar
                </h3>
                <p className="mt-1 text-xs text-ink-muted group-hover:text-ink-secondary">
                  Display fundraising progress with a customizable goal tracker and donation meter
                </p>
              </div>
            </div>
          </div>

          {/* Position 3: Support List */}
          <div
            onClick={() => handleElementTypeSelect('support-list')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleElementTypeSelect('support-list');
              }
            }}
            role="button"
            tabIndex={0}
            className="group cursor-pointer rounded-lg border-2 border-edge bg-paper-inset p-2 sm:p-6 transition-all duration-200 hover:border-ink-muted hover:bg-paper-muted focus:outline-none focus:ring-2 focus:ring-sage"
          >
            <div className="flex items-start gap-4 sm:flex-col sm:items-center sm:gap-0 sm:text-center">
              <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full sm:mx-auto sm:mb-1.5">
                <UserGroupIcon className="h-12 w-12 text-ink-secondary" />
              </div>
              <div className="flex-1 sm:w-full">
                <h3 className="text-sm font-semibold text-ink group-hover:text-ink">
                  Supporter List
                </h3>
                <p className="mt-1 text-xs text-ink-muted group-hover:text-ink-secondary">
                  Showcase your donors and supporters with a dynamic, customizable donor wall
                </p>
              </div>
            </div>
          </div>

          {/* Position 4: QR Code */}
          <div
            onClick={() => handleElementTypeSelect('qr-code')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleElementTypeSelect('qr-code');
              }
            }}
            role="button"
            tabIndex={0}
            className="group cursor-pointer rounded-lg border-2 border-edge bg-paper-inset p-2 sm:p-6 transition-all duration-200 hover:border-ink-muted hover:bg-paper-muted focus:outline-none focus:ring-2 focus:ring-sage"
          >
            <div className="flex items-start gap-4 sm:flex-col sm:items-center sm:gap-0 sm:text-center">
              <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full sm:mx-auto sm:mb-1.5">
                <QrCodeIcon className="h-12 w-12 text-ink-secondary" />
              </div>
              <div className="flex-1 sm:w-full">
                <h3 className="text-sm font-semibold text-ink group-hover:text-ink">QR Code</h3>
                <p className="mt-1 text-xs text-ink-muted group-hover:text-ink-secondary">
                  Generate scannable QR codes that direct visitors to your donation forms
                </p>
              </div>
            </div>
          </div>

          {/* Position 5: Fundraising Flag */}
          <div
            onClick={() => handleElementTypeSelect('fundraising-flag')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleElementTypeSelect('fundraising-flag');
              }
            }}
            role="button"
            tabIndex={0}
            className="group cursor-pointer rounded-lg border-2 border-edge bg-paper-inset p-2 sm:p-6 transition-all duration-200 hover:border-ink-muted hover:bg-paper-muted focus:outline-none focus:ring-2 focus:ring-sage"
          >
            <div className="flex items-start gap-4 sm:flex-col sm:items-center sm:gap-0 sm:text-center">
              <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full sm:mx-auto sm:mb-1.5">
                <WindowIcon className="h-12 w-12 text-ink-secondary" />
              </div>
              <div className="flex-1 sm:w-full">
                <h3 className="text-sm font-semibold text-ink group-hover:text-ink">
                  Fundraising Flag
                </h3>
                <p className="mt-1 text-xs text-ink-muted group-hover:text-ink-secondary">
                  Create a sticky side tab that prompts visitors to donate and opens your form when
                  clicked
                </p>
              </div>
            </div>
          </div>

          {/* Position 6: URL */}
          {shareBlocked ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    role="presentation"
                    className="group cursor-not-allowed rounded-lg border-2 border-edge bg-paper-inset p-2 sm:p-6 opacity-50"
                  >
                    <div className="flex items-start gap-4 sm:flex-col sm:items-center sm:gap-0 sm:text-center">
                      <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full sm:mx-auto sm:mb-1.5">
                        <LinkIcon className="h-12 w-12 text-ink-secondary" />
                      </div>
                      <div className="flex-1 sm:w-full">
                        <h3 className="text-sm font-semibold text-ink">URL</h3>
                        <p className="mt-1 text-xs text-ink-muted">
                          Generate shareable URL links that open your donation form directly when clicked
                        </p>
                      </div>
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p>{URL_SHARE_GATED_TOOLTIP}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <div
              onClick={() => handleElementTypeSelect('url')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleElementTypeSelect('url');
                }
              }}
              role="button"
              tabIndex={0}
              className="group cursor-pointer rounded-lg border-2 border-edge bg-paper-inset p-2 sm:p-6 transition-all duration-200 hover:border-ink-muted hover:bg-paper-muted focus:outline-none focus:ring-2 focus:ring-sage"
            >
              <div className="flex items-start gap-4 sm:flex-col sm:items-center sm:gap-0 sm:text-center">
                <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full sm:mx-auto sm:mb-1.5">
                  <LinkIcon className="h-12 w-12 text-ink-secondary" />
                </div>
                <div className="flex-1 sm:w-full">
                  <h3 className="text-sm font-semibold text-ink group-hover:text-ink">URL</h3>
                  <p className="mt-1 text-xs text-ink-muted group-hover:text-ink-secondary">
                    Generate shareable URL links that open your donation form directly when clicked
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ElementSelectionModal;
